import React, { DragEvent, useEffect, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import ModalConfirmRemovePhoto from "../ModalConfirmRemovePhoto";
import {
  FiChevronDown,
  FiDownload,
  FiImage,
  FiTrash2,
  FiZap,
} from "react-icons/fi";

interface IPhotos {
  id: number;
  url_m: string;
  url_l: string;
}

interface IImageBox {
  photo: IPhotos;
  boxNumber: number;
  isLoading?: boolean;
  handleDrag: (ev: DragEvent<HTMLDivElement>) => void;
  handleDrop: (ev: DragEvent<HTMLDivElement>) => void;
  handleRemovePhoto: () => void;
  onVirtualStaging: (photoId: number) => void;
  onCustomEdit: (photoId: number) => void;
  isGeneratingAI?: boolean;
}

function stopDragPropagation(ev: React.MouseEvent) {
  ev.stopPropagation();
}

const ImageBox: React.FC<IImageBox> = ({
  photo,
  boxNumber,
  isLoading,
  handleDrag,
  handleDrop,
  handleRemovePhoto,
  onVirtualStaging,
  onCustomEdit,
  isGeneratingAI = false,
}) => {
  const [openModalConfirmRemovePhoto, setOpenModalConfirmRemovePhoto] =
    useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  useEffect(() => {
    if (!aiMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAiMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aiMenuOpen]);

  return (
    <div
      className="group relative flex w-full cursor-grab flex-col rounded-xl border border-slate-200 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md active:cursor-grabbing"
      draggable
      id={String(boxNumber)}
      onDragOver={(ev) => ev.preventDefault()}
      onDragStart={handleDrag}
      onDrop={handleDrop}
    >
      {/* Sem overflow-hidden no cartão: o menu de IA fica absolute abaixo do botão e seria recortado. */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-xl bg-slate-100">
        <img
          src={photo.url_m}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
        <p className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
          Arraste para reordenar
        </p>
      </div>

      <div className="flex flex-col gap-2 border-t border-slate-100 p-2">
        <div className="flex items-center justify-center gap-1">
          <a
            href={photo.url_l}
            target="_blank"
            rel="noopener noreferrer"
            title="Baixar imagem"
            onMouseDown={stopDragPropagation}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <FiDownload className="h-[18px] w-[18px]" aria-hidden />
            <span className="sr-only">Baixar</span>
          </a>
          <button
            type="button"
            title="Remover foto"
            onMouseDown={stopDragPropagation}
            onClick={() => setOpenModalConfirmRemovePhoto(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-red-50 hover:text-red-600"
          >
            <FiTrash2 className="h-[18px] w-[18px]" aria-hidden />
            <span className="sr-only">Remover</span>
          </button>
        </div>

        <div className={`relative ${aiMenuOpen ? "z-50" : ""}`}>
          <OutsideClickHandler
            onOutsideClick={() => setAiMenuOpen(false)}
            disabled={!aiMenuOpen}
          >
            <button
              type="button"
              disabled={isGeneratingAI}
              aria-expanded={aiMenuOpen}
              aria-haspopup="menu"
              onMouseDown={stopDragPropagation}
              onClick={() => setAiMenuOpen((o) => !o)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiZap className="h-3.5 w-3.5 text-amber-600" aria-hidden />
              {isGeneratingAI ? "Gerando…" : "Opções com IA"}
              <FiChevronDown
                className={`h-3.5 w-3.5 shrink-0 transition ${aiMenuOpen ? "rotate-180" : ""}`}
                aria-hidden
              />
            </button>

            {aiMenuOpen && !isGeneratingAI && (
              <ul
                role="menu"
                className="absolute left-0 right-0 top-full z-[100] mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left text-xs shadow-lg ring-1 ring-black/5"
              >
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-slate-700 transition hover:bg-slate-50"
                    onMouseDown={stopDragPropagation}
                    onClick={() => {
                      onVirtualStaging(photo.id);
                      setAiMenuOpen(false);
                    }}
                  >
                    <FiImage className="h-4 w-4 shrink-0 text-slate-500" />
                    Adicionar mobília com IA
                  </button>
                </li>
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-slate-700 transition hover:bg-slate-50"
                    onMouseDown={stopDragPropagation}
                    onClick={() => {
                      onCustomEdit(photo.id);
                      setAiMenuOpen(false);
                    }}
                  >
                    <FiZap className="h-4 w-4 shrink-0 text-amber-600" />
                    Editar com instruções (IA)
                  </button>
                </li>
              </ul>
            )}
          </OutsideClickHandler>
        </div>
      </div>

      <ModalConfirmRemovePhoto
        open={openModalConfirmRemovePhoto}
        isLoading={!!isLoading}
        functionToCloseModal={() => setOpenModalConfirmRemovePhoto(false)}
        functionToRemovePhoto={handleRemovePhoto}
      />
    </div>
  );
};

export default ImageBox;
